import { labeebFieldProblemsOf } from "../../api/labeeb";
import { Button } from "../../f7";
import type { WriteState } from "../../hooks/useWrite";
import { Notice } from "../ui";

/**
 * Whatever went wrong with a write, said out loud.
 *
 * WHY THIS EXISTS. Every write control branched on the handful of states its
 * author happened to think of — `BlockControl` and `CapEditor` on two,
 * `AppAccessControl` on four, out of `useWrite`'s fifteen. The states nobody
 * listed rendered NOTHING: a 500, a 503, an expired session or a throttled
 * request left the dialog open with no message, so the operator pressed the
 * button and nothing happened. Reported from dev on the AI-apps allowlist,
 * and the same gap sat on the merchant blocklist, where a silently failed
 * block during an incident is the worst version of it.
 *
 * One component, so the failure path is written once and a control cannot
 * quietly omit it. `useWrite` gaining a state means changing this file, not
 * auditing every caller.
 *
 * SUCCESS IS NOT HERE. `done` has a `WriteResult` with the effect timing and
 * per-control wording; mixing the two would make this the component every
 * write renders twice.
 *
 * Returns null for the three non-failures (`idle`, `submitting`, `done`), so
 * a caller can render it unconditionally.
 *
 * `onReread` is for a control holding a versioned document: a version
 * conflict is a dead end without a way to fetch the version that won.
 * `onDismiss` is for a failure shown on the page rather than in the dialog,
 * which would otherwise sit there until the next write.
 */
export function WriteFailure({
  state,
  onReread,
  onDismiss,
}: {
  state: WriteState;
  onReread?: () => void;
  onDismiss?: () => void;
}) {
  const message = failureMessage(state, onReread);
  if (!message) return null;
  return (
    <Failure tone={message.tone} title={message.title} onDismiss={onDismiss}>
      {message.body}
    </Failure>
  );
}

function failureMessage(
  state: WriteState,
  onReread: (() => void) | undefined,
): { title: string; tone?: "red" | "amber"; body: React.ReactNode } | null {
  switch (state.kind) {
    case "idle":
    case "submitting":
    case "done":
      return null;

    case "signedout":
      return { title: "Not written", body: "Your session ended. Sign in again and retry." };

    case "idleReauth":
      // Distinct from `signedout`: the operator IS signed in, the write was
      // simply not trusted after an idle gap. Telling them their session
      // ended sends them looking for a sign-in page already on its way.
      return {
        title: "Nothing was written",
        tone: "amber",
        body: "This session has been idle, so the change was not sent. Confirming your identity now — retry once you are back.",
      };

    case "rateLimited":
      // The gateway throttling, not a product. There is no Retry-After to
      // quote, so this does not invent a number.
      return {
        title: "Too many changes just now",
        tone: "amber",
        body: "The gateway is throttling requests. Nothing was written — wait a moment and retry.",
      };

    case "versionConflict":
      return {
        title: "Somebody changed this first",
        tone: "amber",
        body: (
          <>
            Nothing was written — this was changed by someone else
            {state.currentVersion !== undefined ? ` (now version ${state.currentVersion})` : ""}.
            Re-read it and decide again.
            {onReread && (
              <div className="mt-2">
                <Button outline onClick={onReread}>
                  Re-read and try again
                </Button>
              </div>
            )}
          </>
        ),
      };

    case "alreadyInState":
      // NOT a failure to the operator: the resource already holds what they
      // asked for. Saying "refused" sends them hunting a problem that does
      // not exist.
      return { title: "Already the case", body: "Nothing needed changing." };

    case "idempotencyConflict":
      return {
        title: "Not written",
        tone: "amber",
        body: "A request key was reused for a different change. Retry; if this repeats it is a bug in this console, not in your change.",
      };

    case "guardRefused":
      // The body names what the refusal would break — the entire point of
      // the guard, so it is surfaced verbatim.
      return { title: "Refused by a guard", body: state.error.message };

    case "invalid": {
      const problems = labeebFieldProblemsOf(state.error.body);
      return {
        title: "Refused",
        body: (
          <>
            {state.error.message}
            {problems.length > 0 && (
              <ul className="mt-1 list-disc pl-5">
                {problems.map((p, i) => (
                  <li key={`${p.field}-${i}`}>
                    <span className="ref">{p.field}</span>: {p.problem}
                  </li>
                ))}
              </ul>
            )}
          </>
        ),
      };
    }

    case "denied":
      return { title: "Not allowed", body: state.error.message };

    case "notfound":
      return { title: "Not found", body: state.error.message };

    case "unavailable":
      return { title: "Could not reach it", body: state.error.message };

    case "error":
      return { title: "Not written", body: state.error.message };
  }
}

function Failure({
  title,
  tone = "red",
  onDismiss,
  children,
}: {
  title: string;
  tone?: "red" | "amber";
  onDismiss?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-2">
      <Notice tone={tone} title={title}>
        {children}
        {onDismiss && (
          <div className="mt-2">
            <Button plain onClick={onDismiss}>
              Dismiss
            </Button>
          </div>
        )}
      </Notice>
    </div>
  );
}
